import { useState } from "react";
import { useTranslation } from "react-i18next";
import { switchLocale } from "../../locale/i18n.js";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LOCALES, LOCALE_FLAG, LOCALE_LABEL, readLocale, selectLocale, type Locale } from "../../locale/locale.js";

/**
 * Language: **English / Deutsch** (owner decision — those two only).
 *
 * Items are labelled with ENDONYMS — "Deutsch", not "German". A language picker names each language in that
 * language, because someone who cannot read the current UI language still needs to find their own.
 *
 * Which is why `LOCALE_LABEL` stays a plain constant and is NOT translated: an endonym is the same word in every
 * catalogue by definition, so routing it through `t()` would add a way for the two to disagree and no way for them
 * to improve. The surrounding chrome — the heading, the trigger's accessible name — is translated.
 *
 * Switching now does three things: sets `<html lang>` (screen readers take pronunciation from it), records the
 * choice, and loads + activates the locale in i18next (ADR-048 Phase 2). The load is the reason this is async —
 * a locale's catalogue is fetched on first use, and its tenant overlay is installed in the same step so the
 * terminology can never lag a language behind.
 */
export function LanguageMenu({ defaultLocale, realm }: { defaultLocale?: Locale; realm?: string }) {
  const { t, i18n } = useTranslation();
  // The SAME pure resolution `main.tsx` ran at boot, with the same inputs — so the trigger can never disagree with
  // the `<html lang>` that is already applied. `defaultLocale` is the tenant's (ADR-047 D2); it is the last step of
  // the chain, so an explicit browser or stored preference still wins.
  const [locale, setLocale] = useState<Locale>(() => readLocale(defaultLocale));

  return (
    <DropdownMenuTrigger>
      <Button variant="ghost" className="gap-1.5 px-2" aria-label={t("language.current", { language: LOCALE_LABEL[locale] })}>
        {/* The flag replaces the globe once a language is chosen — decorative either way. */}
        <span aria-hidden="true" className="text-sm leading-none">
          {LOCALE_FLAG[locale]}
        </span>
        {/* The uppercased tag is the compact affordance; the accessible name above carries the full language. */}
        <span aria-hidden="true" className="text-xs font-medium max-sm:hidden">
          {locale.toUpperCase()}
        </span>
      </Button>

      <DropdownMenu
        // Size to content, not the tiny icon trigger: the default `w-(--trigger-width)` floors at min-w-32 (128px),
        // too narrow for "🇩🇪 Deutsch" + the DE tag + the single-selection check reservation (pr-8). The content then
        // overflowed, `ml-auto` collapsed, and the tag slid under the check on the selected row (measured: 6px).
        className="w-auto min-w-40"
        selectionMode="single"
        selectedKeys={[locale]}
        onSelectionChange={(keys) => {
          // Re-pressing the ACTIVE item emits an EMPTY set — react-aria's single-selection menu allows deselection
          // (`disallowEmptySelection` defaults to false). That press is still a deliberate choice of the current
          // language, so it resolves to `locale` rather than being discarded (diff panel).
          //
          // Discarding it left the auto-resolved default impossible to PIN: nothing persists at boot any more
          // (ADR-047 D2), so a user on an Italian browser who is shown German and clicks "Deutsch" to keep it
          // would write nothing — and a later switch to an English browser would silently flip them to English,
          // because the chain puts the browser above the tenant. Confirming the active item is the only way to
          // say "this one, regardless of what my browser reports", so it must record.
          const next = ([...keys][0] as Locale | undefined) ?? locale;
          setLocale(next);
          // `selectLocale`, not `applyLocale` — this is the ONE place a locale may be written to storage, because
          // it is the one place the user actually chose it (ADR-047 D2).
          selectLocale(next);
          // The catalogue swap. Deliberately not awaited: `<html lang>` and the trigger update immediately, and
          // i18next re-renders its subscribers when the resources land. A rejection here means the chunk could
          // not be fetched — the previous language stays on screen, which is the right degradation.
          void switchLocale(i18n, next, realm);
        }}
      >
        <DropdownMenuLabel>{t("language.selectLanguageTitle")}</DropdownMenuLabel>
        {LOCALES.map((l) => (
          <DropdownMenuItem key={l} id={l} textValue={LOCALE_LABEL[l]}>
            {/* aria-hidden: a flag is a country, not a language. The endonym beside it is the real label. */}
            <span aria-hidden="true" className="text-sm leading-none">
              {LOCALE_FLAG[l]}
            </span>
            {LOCALE_LABEL[l]}
            <span aria-hidden="true" className="text-muted-foreground ml-auto text-xs">
              {l.toUpperCase()}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </DropdownMenuTrigger>
  );
}
